/**
 * Self-service MFA ของผู้ที่เข้าสู่ระบบแล้ว: ดูสถานะ, enrol, สร้างรหัสกู้คืนชุดใหม่
 *
 * ใช้ bearer ของ session เต็มสิทธิ์ผ่าน `mfaClient` เท่านั้น ความลับทั้งหมดอยู่ใน state
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import type { DeviceKind } from './client';
import './mfa.css';
import { MfaEnrolFlow, RecoveryCodesSheet, TotpInput } from './MfaParts';
import { MFA_CODE_LENGTH, describeMfaFailure } from './mfaMessages';
import {
  confirmMyEnrolment,
  readMyMfa,
  regenerateMyRecoveryCodes,
  startMyEnrolment,
  type MfaStatus,
} from './mfaClient';
import { clearMfaEnrolmentSuggested, isMfaEnrolmentSuggested } from './mfaNotice';
import { useSession } from './session';

type View =
  | { readonly kind: 'closed' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'status'; readonly status: MfaStatus }
  | { readonly kind: 'enrol'; readonly currentCode: string | undefined }
  | { readonly kind: 'ask-code'; readonly purpose: 'enrol' | 'regenerate' }
  | { readonly kind: 'codes'; readonly codes: readonly string[] };

export interface MyMfaPanelProps {
  readonly deviceKind: DeviceKind;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function MyMfaPanel({ deviceKind, apiBaseUrl, fetchImpl }: MyMfaPanelProps) {
  const { token } = useSession();
  const [view, setView] = useState<View>({ kind: 'closed' });
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggested] = useState(isMfaEnrolmentSuggested);

  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  const load = useCallback(() => {
    if (token === null) {
      return;
    }
    setView({ kind: 'loading' });
    void readMyMfa(token, {
      ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
      ...(fetchImpl === undefined ? {} : { fetchImpl }),
    }).then((result) => {
      setView(
        result.status === 'ok'
          ? { kind: 'status', status: result.value }
          : { kind: 'error', message: describeMfaFailure(result.failure) },
      );
    });
  }, [token, apiBaseUrl, fetchImpl]);

  // โหลดสถานะเมื่อผู้ใช้เปิดส่วนนี้เท่านั้น — ไม่ยิงคำขอเองตอน panel รหัสผ่านถูกเปิด
  useEffect(() => {
    if (view.kind !== 'closed') {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  if (token === null) {
    return null;
  }

  const finishCodes = () => {
    clearMfaEnrolmentSuggested();
    setCode('');
    load();
  };

  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (view.kind !== 'ask-code' || busy || code.length !== MFA_CODE_LENGTH) {
      return;
    }
    const entered = code;
    setCode('');
    if (view.purpose === 'enrol') {
      // เริ่มใหม่ขณะมี factor ที่ใช้งานอยู่ต้องยืนยันด้วยรหัสปัจจุบัน (ตรวจที่เซิร์ฟเวอร์ตอน start)
      setView({ kind: 'enrol', currentCode: entered });
      return;
    }
    setBusy(true);
    setError(null);
    void regenerateMyRecoveryCodes(token, entered, options).then((result) => {
      setBusy(false);
      if (result.status === 'ok') {
        setView({ kind: 'codes', codes: result.value.recoveryCodes });
        return;
      }
      setError(describeMfaFailure(result.failure));
    });
  };

  let body;
  if (view.kind === 'closed') {
    body = (
      <button type="button" className="cap-mfa__secondary" data-testid="my-mfa-open" onClick={load}>
        จัดการการยืนยันตัวตนสองขั้น
      </button>
    );
  } else if (view.kind === 'loading') {
    body = (
      <p className="cap-mfa__sub" role="status" data-testid="my-mfa-loading">
        กำลังโหลดสถานะ…
      </p>
    );
  } else if (view.kind === 'error') {
    body = (
      <>
        <p className="cap-mfa__error" role="alert" data-testid="mfa-error">
          {view.message}
        </p>
        <button type="button" className="cap-mfa__link" data-testid="my-mfa-retry" onClick={load}>
          ลองอีกครั้ง
        </button>
      </>
    );
  } else if (view.kind === 'enrol') {
    const currentCode = view.currentCode;
    body = (
      <MfaEnrolFlow
        deviceKind={deviceKind}
        start={() => startMyEnrolment(token, currentCode, options)}
        confirm={(entered) => confirmMyEnrolment(token, entered, options)}
        onFinished={finishCodes}
        onCancel={load}
      />
    );
  } else if (view.kind === 'codes') {
    body = <RecoveryCodesSheet codes={view.codes} deviceKind={deviceKind} onAcknowledged={finishCodes} />;
  } else if (view.kind === 'ask-code') {
    body = (
      <form className="cap-mfa__form" method="post" onSubmit={submitCode} aria-label="ยืนยันรหัสปัจจุบัน">
        <div className="cap-mfa__field">
          <label className="cap-mfa__label" htmlFor="cap-my-mfa-current">
            รหัส 6 หลักปัจจุบันจากแอป
          </label>
          <TotpInput
            id="cap-my-mfa-current"
            value={code}
            onChange={setCode}
            disabled={busy}
            deviceKind={deviceKind}
          />
        </div>
        {error === null ? null : (
          <p className="cap-mfa__error" role="alert" data-testid="mfa-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="cap-mfa__primary"
          disabled={busy || code.length !== MFA_CODE_LENGTH}
          data-testid="my-mfa-current-submit"
        >
          {busy ? 'กำลังตรวจสอบ…' : 'ดำเนินการต่อ'}
        </button>
        <button
          type="button"
          className="cap-mfa__link"
          data-testid="mfa-cancel"
          onClick={() => {
            setCode('');
            setError(null);
            load();
          }}
        >
          ยกเลิก
        </button>
      </form>
    );
  } else {
    const { status } = view;
    const active = status.status === 'ACTIVE';
    body = (
      <>
        <dl className="cap-mfa__status" data-testid="my-mfa-status" data-state={status.status ?? 'NONE'}>
          <dt>สถานะ</dt>
          <dd>{active ? 'เปิดใช้งานแล้ว' : status.status === 'PENDING' ? 'ตั้งค่าค้างอยู่' : 'ยังไม่ได้ตั้งค่า'}</dd>
          {active ? (
            <>
              <dt>รหัสกู้คืนที่เหลือ</dt>
              <dd data-testid="my-mfa-remaining">{status.recoveryCodesRemaining}</dd>
            </>
          ) : null}
          {status.locked ? (
            <>
              <dt>การล็อก</dt>
              <dd>ถูกล็อกชั่วคราว</dd>
            </>
          ) : null}
        </dl>
        {active ? (
          <>
            <button
              type="button"
              className="cap-mfa__secondary"
              data-testid="my-mfa-regenerate"
              onClick={() => {
                setError(null);
                setView({ kind: 'ask-code', purpose: 'regenerate' });
              }}
            >
              สร้างรหัสกู้คืนชุดใหม่
            </button>
            <button
              type="button"
              className="cap-mfa__link"
              data-testid="my-mfa-reenrol"
              onClick={() => {
                setError(null);
                setView({ kind: 'ask-code', purpose: 'enrol' });
              }}
            >
              ตั้งค่าแอปยืนยันตัวตนใหม่
            </button>
          </>
        ) : (
          <button
            type="button"
            className="cap-mfa__primary"
            data-testid="my-mfa-enrol"
            onClick={() => {
              setView({ kind: 'enrol', currentCode: undefined });
            }}
          >
            ตั้งค่าการยืนยันตัวตนสองขั้น
          </button>
        )}
      </>
    );
  }

  return (
    <section className="cap-mfa cap-mfa--panel" data-testid="my-mfa-panel" aria-label="การยืนยันตัวตนสองขั้น">
      <h2 className="cap-mfa__title">การยืนยันตัวตนสองขั้น (MFA)</h2>
      {suggested && view.kind === 'status' && view.status.status !== 'ACTIVE' ? (
        <p className="cap-mfa__notice" role="status" data-testid="my-mfa-suggested">
          ระบบแนะนำให้ตั้งค่าการยืนยันตัวตนสองขั้นสำหรับบัญชีนี้
        </p>
      ) : null}
      {body}
    </section>
  );
}
